/**
 * Counting for the shared Receive/Pick dialog (`components/receiving/ScanCountDialog`).
 *
 * Pure: no React, storage or network. Lines are data-agnostic, so one dialog serves transfers,
 * purchase orders and subrentals. Each record's adapter builds the lines, turns the submission
 * into its own RPC payload and, optionally, resolves codes the lines do not list.
 *
 * Terms:
 * - `expected`: units the line expects in total.
 * - `alreadyDone`: units received (or picked) before this count. The dialog never undoes them.
 * - counted: units in this count. Nothing applies until the user presses Receive (or Pick).
 * - room: `expected - alreadyDone`, the most one count can add to a line.
 */

export type ScanCountVerb = 'Receive' | 'Pick';
export type ScanCountTracking = 'serial' | 'quantity';

/** One unit on a serial line. */
export type ScanCountSerial = {
  id: string;
  /** What the chip shows and what a scan matches (barcode, serial or Portal label). */
  code: string;
  /** Extra detail for the chip's tooltip, such as the manufacturer serial. */
  label?: string;
  /** Received (or picked) before this count: shown, never counted again. */
  done: boolean;
  /** Other codes printed on the unit that should match it too. */
  codes?: readonly string[];
};

export type ScanCountLine = {
  id: string;
  name: string;
  sku?: string;
  tracking: ScanCountTracking;
  expected: number;
  alreadyDone: number;
  /** Serial lines: the units on the line. A lookup may add units the line does not list. */
  serials?: readonly ScanCountSerial[];
  /** Quantity lines: SKUs or barcodes; each scan counts one unit. The line's `sku` matches too. */
  codes?: readonly string[];
};

/** This count on one line. */
export type ScanCountEntry = {
  qty?: number;
  serialIds?: string[];
  /** Units a lookup added that the line does not list: serial id to code, for the chips. */
  unlisted?: { [serialId: string]: string };
};

export type ScanCountCounts = { [lineId: string]: ScanCountEntry };

/** One changed line as the dialog submits it: `qty` on quantity lines, `serialIds` on serial lines. */
export type ScanCountSubmissionLine = { lineId: string; qty?: number; serialIds?: string[] };
export type ScanCountSubmission = ScanCountSubmissionLine[];

/**
 * What a packing-slip match fills in, in the submission's shape. A quantity replaces the line's
 * count; serials are added to it (listed, not-yet-done units only).
 */
export type ScanCountFill = readonly ScanCountSubmissionLine[];

/**
 * A code the lines do not match, resolved by the record's adapter. A `serialId` the line does not
 * list is counted as an extra unit on that line and shown with `code` (or the scanned code).
 */
export type ScanCountResolution = { lineId: string; serialId?: string; code?: string };

/**
 * What `onSubmit` may return. Nothing (or `{}`) means every submitted line applied.
 * `appliedLineIds` without `error` lists the lines that applied when only some did.
 * `outcomeUnknown`: no answer came back (the connection dropped), so the submission may have
 * applied. The dialog then keeps that exact submission and offers "Try again", which sends it again
 * with `{ retry: true }`; the record's adapter must resend the same payload under the same request
 * key, so it can never apply twice. Any change to the count makes the next submit a new one.
 */
export type ScanCountSubmitOutcome = { appliedLineIds?: string[]; error?: string; outcomeUnknown?: boolean } | void;

/** How `onSubmit` was reached: `retry` resends, unchanged, the submission whose outcome was unknown. */
export type ScanCountSubmitAttempt = { retry: boolean };

export type ScanCountProblemKind = 'damaged' | 'missing' | 'extra' | 'other';
export type ScanCountProblem = { kind: ScanCountProblemKind; note: string; lineId?: string };

export type ScanCountResultKind =
  | 'counted'
  | 'already-counted'
  | 'already-done'
  | 'line-full'
  | 'needs-serial'
  | 'no-match'
  | 'lookup-failed'
  | 'empty';

export type ScanCountResult = {
  kind: ScanCountResultKind;
  /** The code belongs to a line. */
  matched: boolean;
  /** The count went up. */
  changed: boolean;
  /** The code as shown back to the user. */
  code: string;
  lineId?: string;
  serialId?: string;
  /** Headline, e.g. "RW-2206 received". */
  message: string;
  /** Second line, e.g. "Chauvet Rogue R2 Wash · 6 of 8 on this line". */
  detail?: string;
};

export type ScanCountApplied = { counts: ScanCountCounts; result: ScanCountResult };

export type ScanCountTotals = {
  /** Units in, including this count (never more than a line expects). */
  done: number;
  expected: number;
  /** Units still due after this count. */
  remaining: number;
  /** Units in this count: what pressing Receive (or Pick) applies. */
  pending: number;
};

export type ScanCountLineState = {
  counted: number;
  /** `alreadyDone` plus this count (may exceed `expected` when a line was over-received). */
  done: number;
  room: number;
  complete: boolean;
  serialIds: string[];
  unlisted: { [serialId: string]: string };
};

type PlainObject = { [key: string]: unknown };

function isPlainObject(value: unknown): value is PlainObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** A whole number of units, never negative; anything else counts as 0. */
function whole(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
}

function lineLabel(line: ScanCountLine): string {
  return line.name || line.sku || 'This line';
}

export function normalizeScanCode(code: string | null | undefined): string {
  return String(code ?? '').trim().toLowerCase();
}

export function scanCountPastTense(verb: ScanCountVerb): 'received' | 'picked' {
  return verb === 'Pick' ? 'picked' : 'received';
}

/** "1 unit", "4 units". */
export function scanCountUnits(count: number): string {
  return `${count} ${count === 1 ? 'unit' : 'units'}`;
}

/** The most one count can add to a line. */
export function scanCountLineRoom(line: Pick<ScanCountLine, 'expected' | 'alreadyDone'>): number {
  return Math.max(0, whole(line.expected) - whole(line.alreadyDone));
}

/** A typed or stepped quantity as a whole number between 0 and the line's room. */
export function clampQty(line: Pick<ScanCountLine, 'expected' | 'alreadyDone'>, value: unknown): number {
  return Math.min(scanCountLineRoom(line), whole(value));
}

/** An empty count: every quantity at 0, no serials. */
export function initialCounts(lines: readonly ScanCountLine[]): ScanCountCounts {
  const counts: ScanCountCounts = {};
  for (const line of lines) counts[line.id] = emptyEntry(line);
  return counts;
}

function emptyEntry(line: ScanCountLine): ScanCountEntry {
  return line.tracking === 'serial' ? { serialIds: [] } : { qty: 0 };
}

/**
 * One line's count made consistent with the line as it is now: quantities clamped to the room;
 * serials deduplicated, without units done since, unlisted ones only with a code, and no more than
 * the room allows.
 */
function normalizeEntry(line: ScanCountLine, entry: ScanCountEntry | undefined): ScanCountEntry {
  const room = scanCountLineRoom(line);
  if (line.tracking !== 'serial') return { qty: Math.min(room, whole(entry?.qty)) };
  const listed = new Map<string, ScanCountSerial>();
  for (const serial of line.serials ?? []) listed.set(serial.id, serial);
  const serialIds: string[] = [];
  const unlisted: { [serialId: string]: string } = {};
  const seen = new Set<string>();
  for (const id of entry?.serialIds ?? []) {
    if (serialIds.length >= room) break;
    if (!isNonEmptyString(id) || seen.has(id)) continue;
    const serial = listed.get(id);
    if (serial) {
      if (serial.done) continue;
    } else {
      const code = entry?.unlisted?.[id];
      if (!isNonEmptyString(code)) continue;
      unlisted[id] = code;
    }
    seen.add(id);
    serialIds.push(id);
  }
  return Object.keys(unlisted).length ? { serialIds, unlisted } : { serialIds };
}

/** Every line's count made consistent with the lines as they are now (unknown lines dropped). */
export function normalizeScanCounts(lines: readonly ScanCountLine[], counts: ScanCountCounts | null | undefined): ScanCountCounts {
  const next: ScanCountCounts = {};
  for (const line of lines) next[line.id] = normalizeEntry(line, counts?.[line.id]);
  return next;
}

export function scanCountLineState(line: ScanCountLine, entry: ScanCountEntry | undefined): ScanCountLineState {
  const normal = normalizeEntry(line, entry);
  const counted = line.tracking === 'serial' ? normal.serialIds?.length ?? 0 : normal.qty ?? 0;
  const done = whole(line.alreadyDone) + counted;
  return {
    counted,
    done,
    room: scanCountLineRoom(line),
    complete: done >= whole(line.expected),
    serialIds: normal.serialIds ?? [],
    unlisted: normal.unlisted ?? {},
  };
}

export function scanCountTotals(lines: readonly ScanCountLine[], counts: ScanCountCounts): ScanCountTotals {
  let done = 0;
  let expected = 0;
  let remaining = 0;
  let pending = 0;
  for (const line of lines) {
    const state = scanCountLineState(line, counts[line.id]);
    const lineExpected = whole(line.expected);
    expected += lineExpected;
    pending += state.counted;
    done += Math.min(lineExpected, state.done);
    remaining += Math.max(0, lineExpected - state.done);
  }
  return { done, expected, remaining, pending };
}

export function hasPendingScanCounts(lines: readonly ScanCountLine[], counts: ScanCountCounts): boolean {
  return lines.some((line) => scanCountLineState(line, counts[line.id]).counted > 0);
}

/** The changed lines only: quantity lines with `qty`, serial lines with `serialIds`. */
export function countsToSubmission(lines: readonly ScanCountLine[], counts: ScanCountCounts): ScanCountSubmission {
  const submission: ScanCountSubmission = [];
  for (const line of lines) {
    const state = scanCountLineState(line, counts[line.id]);
    if (!state.counted) continue;
    if (line.tracking === 'serial') submission.push({ lineId: line.id, serialIds: [...state.serialIds] });
    else submission.push({ lineId: line.id, qty: state.counted });
  }
  return submission;
}

function progressDetail(line: ScanCountLine, counted: number): string {
  return `${lineLabel(line)} · ${whole(line.alreadyDone) + counted} of ${whole(line.expected)} on this line`;
}

function noMatch(code: string): ScanCountResult {
  return {
    kind: 'no-match',
    matched: false,
    changed: false,
    code,
    message: `No line matches ${code}`,
    detail: 'Check the code, or count it by hand in Count mode.',
  };
}

function needsSerial(line: ScanCountLine, code: string): ScanCountResult {
  return {
    kind: 'needs-serial',
    matched: true,
    changed: false,
    code,
    lineId: line.id,
    message: `Scan each unit's own code`,
    detail: `${lineLabel(line)} is counted by unit, so scan the serial or barcode on each one.`,
  };
}

/** The result for a lookup (`resolveScan`) that failed; `reason` is already readable. */
export function scanLookupFailed(code: string, reason: string): ScanCountResult {
  return { kind: 'lookup-failed', matched: false, changed: false, code, message: `Could not look up ${code}`, detail: reason };
}

type Unit = { id: string; code: string; done: boolean; listed: boolean };

function countUnit(
  counts: ScanCountCounts,
  line: ScanCountLine,
  unit: Unit,
  verb: ScanCountVerb,
): ScanCountApplied {
  const entry = normalizeEntry(line, counts[line.id]);
  const serialIds = entry.serialIds ?? [];
  const past = scanCountPastTense(verb);
  const base = { matched: true, code: unit.code, lineId: line.id, serialId: unit.id };
  if (unit.done) {
    return { counts, result: { ...base, kind: 'already-done', changed: false, message: `${unit.code} was already ${past}`, detail: progressDetail(line, serialIds.length) } };
  }
  if (serialIds.includes(unit.id)) {
    return { counts, result: { ...base, kind: 'already-counted', changed: false, message: `${unit.code} is already counted`, detail: progressDetail(line, serialIds.length) } };
  }
  if (serialIds.length >= scanCountLineRoom(line)) {
    return { counts, result: { ...base, kind: 'line-full', changed: false, message: `No room for ${unit.code}`, detail: `${lineLabel(line)} already has all ${whole(line.expected)}.` } };
  }
  const next: ScanCountEntry = { serialIds: [...serialIds, unit.id] };
  const unlisted = unit.listed ? entry.unlisted : { ...(entry.unlisted ?? {}), [unit.id]: unit.code };
  if (unlisted && Object.keys(unlisted).length) next.unlisted = unlisted;
  return {
    counts: { ...counts, [line.id]: next },
    result: { ...base, kind: 'counted', changed: true, message: `${unit.code} ${past}`, detail: progressDetail(line, serialIds.length + 1) },
  };
}

function countOne(counts: ScanCountCounts, line: ScanCountLine, code: string, verb: ScanCountVerb): ScanCountApplied {
  const qty = normalizeEntry(line, counts[line.id]).qty ?? 0;
  const base = { matched: true, code, lineId: line.id };
  if (qty >= scanCountLineRoom(line)) {
    return { counts, result: { ...base, kind: 'line-full', changed: false, message: `No room for ${code}`, detail: `${lineLabel(line)} already has all ${whole(line.expected)}.` } };
  }
  return {
    counts: { ...counts, [line.id]: { qty: qty + 1 } },
    result: { ...base, kind: 'counted', changed: true, message: `${code} ${scanCountPastTense(verb)}`, detail: progressDetail(line, qty + 1) },
  };
}

function serialMatches(serial: ScanCountSerial, key: string): boolean {
  if (normalizeScanCode(serial.code) === key) return true;
  return (serial.codes ?? []).some((code) => normalizeScanCode(code) === key);
}

/** The line's own spelling of a code it answers to (its SKU or one of its codes), if any. */
function matchedLineCode(line: ScanCountLine, key: string): string | null {
  if (line.sku && normalizeScanCode(line.sku) === key) return line.sku.trim();
  const code = (line.codes ?? []).find((candidate) => normalizeScanCode(candidate) === key);
  return code ? code.trim() : null;
}

/**
 * Applies one scanned or typed code, matching (case-insensitive, trimmed) a listed unit first, then
 * a unit a lookup already added, then a line's SKU or codes. A quantity line counts one unit per
 * scan; when several lines share a code, the first with room takes it.
 */
export function applyScan(
  lines: readonly ScanCountLine[],
  counts: ScanCountCounts,
  code: string,
  verb: ScanCountVerb = 'Receive',
): ScanCountApplied {
  const shown = String(code ?? '').trim();
  const key = normalizeScanCode(shown);
  if (!key) return { counts, result: { kind: 'empty', matched: false, changed: false, code: '', message: 'Scan or type a code' } };

  const unitHits: Array<{ line: ScanCountLine; serial: ScanCountSerial }> = [];
  for (const line of lines) {
    if (line.tracking !== 'serial') continue;
    for (const serial of line.serials ?? []) if (serialMatches(serial, key)) unitHits.push({ line, serial });
  }
  if (unitHits.length) {
    const countable = unitHits.find(({ line, serial }) => {
      const entry = normalizeEntry(line, counts[line.id]);
      return !serial.done && !(entry.serialIds ?? []).includes(serial.id) && (entry.serialIds ?? []).length < scanCountLineRoom(line);
    });
    const { line, serial } = countable ?? unitHits[0];
    return countUnit(counts, line, { id: serial.id, code: serial.code, done: serial.done, listed: true }, verb);
  }

  for (const line of lines) {
    if (line.tracking !== 'serial') continue;
    const unlisted = normalizeEntry(line, counts[line.id]).unlisted ?? {};
    for (const id of Object.keys(unlisted)) {
      if (normalizeScanCode(unlisted[id]) === key) return countUnit(counts, line, { id, code: unlisted[id], done: false, listed: false }, verb);
    }
  }

  const codeHits = lines.filter((line) => matchedLineCode(line, key) !== null);
  const quantityHits = codeHits.filter((line) => line.tracking !== 'serial');
  if (quantityHits.length) {
    const withRoom = quantityHits.find((line) => (normalizeEntry(line, counts[line.id]).qty ?? 0) < scanCountLineRoom(line));
    const line = withRoom ?? quantityHits[0];
    return countOne(counts, line, matchedLineCode(line, key) ?? shown, verb);
  }
  if (codeHits.length) return { counts, result: needsSerial(codeHits[0], shown) };
  return { counts, result: noMatch(shown) };
}

/** Applies what the adapter's `resolveScan` found for a code `applyScan` could not match. */
export function applyScanResolution(
  lines: readonly ScanCountLine[],
  counts: ScanCountCounts,
  code: string,
  resolution: ScanCountResolution | null | undefined,
  verb: ScanCountVerb = 'Receive',
): ScanCountApplied {
  const shown = String(code ?? '').trim();
  const line = resolution ? lines.find((candidate) => candidate.id === resolution.lineId) : undefined;
  if (!resolution || !line) return { counts, result: noMatch(shown) };
  if (line.tracking !== 'serial') return countOne(counts, line, shown, verb);
  if (!resolution.serialId) return { counts, result: needsSerial(line, shown) };
  const listed = (line.serials ?? []).find((serial) => serial.id === resolution.serialId);
  if (listed) return countUnit(counts, line, { id: listed.id, code: listed.code, done: listed.done, listed: true }, verb);
  const unitCode = String(resolution.code ?? '').trim() || shown;
  return countUnit(counts, line, { id: resolution.serialId, code: unitCode, done: false, listed: false }, verb);
}

/** Count mode's stepper and number field. */
export function setScanCountQty(lines: readonly ScanCountLine[], counts: ScanCountCounts, lineId: string, qty: unknown): ScanCountCounts {
  const line = lines.find((candidate) => candidate.id === lineId);
  if (!line || line.tracking === 'serial') return counts;
  const next = clampQty(line, qty);
  if ((normalizeEntry(line, counts[lineId]).qty ?? 0) === next) return counts;
  return { ...counts, [lineId]: { qty: next } };
}

/** Count mode's serial chips: counts a not-yet-done unit, or takes a counted one back out. */
export function toggleScanCountSerial(lines: readonly ScanCountLine[], counts: ScanCountCounts, lineId: string, serialId: string): ScanCountCounts {
  const line = lines.find((candidate) => candidate.id === lineId);
  if (!line || line.tracking !== 'serial') return counts;
  const entry = normalizeEntry(line, counts[lineId]);
  const serialIds = entry.serialIds ?? [];
  if (serialIds.includes(serialId)) {
    const next: ScanCountEntry = { serialIds: serialIds.filter((id) => id !== serialId) };
    const unlisted = { ...(entry.unlisted ?? {}) };
    delete unlisted[serialId];
    if (Object.keys(unlisted).length) next.unlisted = unlisted;
    return { ...counts, [lineId]: next };
  }
  const serial = (line.serials ?? []).find((candidate) => candidate.id === serialId);
  if (!serial || serial.done || serialIds.length >= scanCountLineRoom(line)) return counts;
  return { ...counts, [lineId]: { ...entry, serialIds: [...serialIds, serialId] } };
}

/** Counts every unit still due: each quantity line to its room, each listed unit not yet done. */
export function countAllScanDue(lines: readonly ScanCountLine[], counts: ScanCountCounts): ScanCountCounts {
  const next: ScanCountCounts = { ...counts };
  for (const line of lines) {
    const room = scanCountLineRoom(line);
    if (line.tracking !== 'serial') {
      next[line.id] = { qty: room };
      continue;
    }
    const entry = normalizeEntry(line, counts[line.id]);
    const serialIds = [...(entry.serialIds ?? [])];
    for (const serial of line.serials ?? []) {
      if (serialIds.length >= room) break;
      if (!serial.done && !serialIds.includes(serial.id)) serialIds.push(serial.id);
    }
    next[line.id] = { ...entry, serialIds };
  }
  return next;
}

/** Fills in a packing-slip match; returns the lines the slip matched. */
export function applyScanCountFill(
  lines: readonly ScanCountLine[],
  counts: ScanCountCounts,
  fill: ScanCountFill | null | undefined,
): { counts: ScanCountCounts; lineIds: string[] } {
  const byId = new Map<string, ScanCountLine>();
  for (const line of lines) byId.set(line.id, line);
  let next = counts;
  const lineIds: string[] = [];
  for (const item of fill ?? []) {
    const line = item ? byId.get(item.lineId) : undefined;
    if (!line) continue;
    if (line.tracking !== 'serial') {
      if (item.qty == null) continue;
      next = { ...next, [line.id]: { qty: clampQty(line, item.qty) } };
    } else {
      const entry = normalizeEntry(line, next[line.id]);
      const serialIds = [...(entry.serialIds ?? [])];
      const room = scanCountLineRoom(line);
      for (const id of item.serialIds ?? []) {
        if (serialIds.length >= room) break;
        const serial = (line.serials ?? []).find((candidate) => candidate.id === id);
        if (serial && !serial.done && !serialIds.includes(id)) serialIds.push(id);
      }
      next = { ...next, [line.id]: { ...entry, serialIds } };
    }
    if (!lineIds.includes(line.id)) lineIds.push(line.id);
  }
  return { counts: next, lineIds };
}

/** Empties the given lines' counts (after they applied) and keeps the rest. */
export function clearScanCountLines(lines: readonly ScanCountLine[], counts: ScanCountCounts, lineIds: readonly string[]): ScanCountCounts {
  const next: ScanCountCounts = { ...counts };
  for (const line of lines) if (lineIds.includes(line.id)) next[line.id] = emptyEntry(line);
  return next;
}

/** Where a viewer's draft lives in localStorage. Every dialog of one record and verb shares it. */
export function scanCountDraftStorageKey(draftKey: string, verb: ScanCountVerb): string {
  return `scanCountDraft:v1:${draftKey}:${verb}`;
}

/** Per line: units done (received or picked) on it when its count began. */
export type ScanCountStamps = { readonly [lineId: string]: number };

/** One line's count in a stored draft, with the units done on that line when the count began. */
export type ScanCountDraftLine = { entry: ScanCountEntry; done: number };

/**
 * A viewer's stored count for one record and verb, line by line. A dialog that shows only some of
 * the lines (Receive selected) reads and writes those lines and keeps the others as they are.
 */
export type ScanCountDraft = { savedAt: string; lines: { [lineId: string]: ScanCountDraftLine } };

export type ScanCountDraftRestore = {
  counts: ScanCountCounts;
  stamps: ScanCountStamps;
  /** Lines whose count came back. */
  restoredLineIds: string[];
  /** Quantity lines whose count was dropped: units were done on them since it was counted. */
  staleLineIds: string[];
};

const DRAFT_VERSION = 2;

/**
 * Stamps for the lines counted now: a line keeps the stamp from when its count began, a line just
 * counted takes the units done on it now, and a line no longer counted loses its stamp.
 */
export function stampScanCounts(lines: readonly ScanCountLine[], counts: ScanCountCounts, stamps: ScanCountStamps): ScanCountStamps {
  const next: { [lineId: string]: number } = {};
  for (const line of lines) {
    if (scanCountLineState(line, counts[line.id]).counted > 0) next[line.id] = stamps[line.id] ?? whole(line.alreadyDone);
  }
  return next;
}

/**
 * Counted quantity lines with units done on them since their count began (another receipt or pick,
 * including one whose answer was lost). Their count can no longer be trusted: some of it may be in
 * already. Serial lines need no stamp: a unit done since simply drops out of the count.
 */
export function staleScanCountLineIds(lines: readonly ScanCountLine[], counts: ScanCountCounts, stamps: ScanCountStamps): string[] {
  return lines
    .filter((line) => line.tracking !== 'serial'
      && stamps[line.id] !== undefined
      && stamps[line.id] !== whole(line.alreadyDone)
      && scanCountLineState(line, counts[line.id]).counted > 0)
    .map((line) => line.id);
}

function sanitizeEntry(value: unknown): ScanCountEntry | null {
  if (!isPlainObject(value)) return null;
  const entry: ScanCountEntry = {};
  if (typeof value.qty === 'number' && Number.isFinite(value.qty)) entry.qty = value.qty;
  if (Array.isArray(value.serialIds)) entry.serialIds = value.serialIds.filter(isNonEmptyString);
  if (isPlainObject(value.unlisted)) {
    const unlisted: { [serialId: string]: string } = {};
    for (const id of Object.keys(value.unlisted)) {
      const code = value.unlisted[id];
      if (id && isNonEmptyString(code)) unlisted[id] = code;
    }
    if (Object.keys(unlisted).length) entry.unlisted = unlisted;
  }
  return entry;
}

/** A stored draft, or null when there is none or it cannot be read (an older format included). */
export function parseScanCountDraft(raw: string | null | undefined): ScanCountDraft | null {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPlainObject(data) || data.v !== DRAFT_VERSION || !isPlainObject(data.lines)) return null;
  const lines: { [lineId: string]: ScanCountDraftLine } = {};
  for (const lineId of Object.keys(data.lines)) {
    const stored = data.lines[lineId];
    if (!isPlainObject(stored)) continue;
    const entry = sanitizeEntry(stored.entry);
    if (!entry || typeof stored.done !== 'number' || !Number.isFinite(stored.done)) continue;
    lines[lineId] = { entry, done: whole(stored.done) };
  }
  return { savedAt: typeof data.savedAt === 'string' ? data.savedAt : '', lines };
}

export function serializeScanCountDraft(draft: ScanCountDraft): string {
  return JSON.stringify({ v: DRAFT_VERSION, savedAt: draft.savedAt, lines: draft.lines });
}

/**
 * The stored counts for the lines shown, as they are now. A line's count comes back only when nothing
 * was done on it since it was counted (quantity lines); a serial line keeps its units still due.
 */
export function restoreScanCountDraft(draft: ScanCountDraft | null, lines: readonly ScanCountLine[]): ScanCountDraftRestore {
  const counts = initialCounts(lines);
  const stamps: { [lineId: string]: number } = {};
  const restoredLineIds: string[] = [];
  const staleLineIds: string[] = [];
  for (const line of lines) {
    const stored = draft?.lines[line.id];
    if (!stored) continue;
    if (line.tracking !== 'serial' && stored.done !== whole(line.alreadyDone)) {
      staleLineIds.push(line.id);
      continue;
    }
    const entry = normalizeEntry(line, stored.entry);
    if (scanCountLineState(line, entry).counted <= 0) continue;
    counts[line.id] = entry;
    stamps[line.id] = stored.done;
    restoredLineIds.push(line.id);
  }
  return { counts, stamps, restoredLineIds, staleLineIds };
}

/**
 * The draft to store after a change: the counted lines shown (with their stamps) plus every line the
 * stored draft holds that this dialog does not show. Null when nothing is left to keep.
 */
export function updateScanCountDraft(
  draft: ScanCountDraft | null,
  lines: readonly ScanCountLine[],
  counts: ScanCountCounts,
  stamps: ScanCountStamps,
  now: Date = new Date(),
): ScanCountDraft | null {
  const shown = new Set(lines.map((line) => line.id));
  const kept: { [lineId: string]: ScanCountDraftLine } = {};
  for (const lineId of Object.keys(draft?.lines ?? {})) {
    if (!shown.has(lineId) && draft) kept[lineId] = draft.lines[lineId];
  }
  for (const line of lines) {
    const entry = normalizeEntry(line, counts[line.id]);
    if (scanCountLineState(line, entry).counted <= 0) continue;
    kept[line.id] = { entry, done: stamps[line.id] ?? whole(line.alreadyDone) };
  }
  return Object.keys(kept).length ? { savedAt: now.toISOString(), lines: kept } : null;
}
